import React from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Monitor,
  BookOpen,
  Server,
  Presentation,
  Theater,
  Coffee,
  Bus,
  Trophy,
  Building2,
} from "lucide-react";
import { FACILITIES, CLUBS, CELLS, COLLEGE } from "../data/college";
import { useT } from "../i18n/LanguageProvider";

const ICONS = {
  Monitor,
  BookOpen,
  Server,
  Presentation,
  Theater,
  Coffee,
  Bus,
  Trophy,
  Building2,
};

const CampusLife = () => {
  const { t, facilityLabel } = useT();
  return (
    <section
      id="campus"
      className="relative py-16 md:py-24 lg:py-32 border-t border-white/5 overflow-hidden"
      data-testid="campus-section"
    >
      {/* BG image with heavy mask */}
      <div className="absolute inset-0">
        <img
          src={COLLEGE.campus_life}
          alt=""
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#04060a] via-[#04060a]/85 to-[#04060a]" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-12"
        >
          <div className="kicker mb-5">{t("campus.kicker")}</div>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
            {t("campus.headA")}{" "}
            <span className="text-[#00E5FF] text-glow-cyan">
              {t("campus.headB")}
            </span>{" "}
            {t("campus.and")}{" "}
            <span className="text-[#39FF14] text-glow-neon">
              {t("campus.headC")}
            </span>
            .
          </h2>
        </motion.div>

        {/* Facilities */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3 mb-16"
        >
          {FACILITIES.map((f, i) => {
            const Icon = ICONS[f.icon] || Monitor;
            return (
              <div
                key={f.name}
                className="group glass !rounded-lg p-4 flex flex-col items-center text-center gap-2.5"
                data-testid={`facility-${i}`}
              >
                <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[#00E5FF]/8 border border-[#00E5FF]/20 group-hover:border-[#39FF14]/50 group-hover:bg-[#39FF14]/10 transition-colors">
                  <Icon className="w-5 h-5 text-[#00E5FF] group-hover:text-[#39FF14] transition-colors" />
                </div>
                <div className="text-[11px] md:text-[12px] font-medium text-white/85 leading-tight">
                  {facilityLabel(f.name)}
                </div>
              </div>
            );
          })}
        </motion.div>

        {/* Clubs Marquee */}
        <div className="mb-12">
          <div className="kicker mb-5">{t("campus.clubsLabel")}</div>
          <div className="marquee-fade">
            <Marquee gradient={false} speed={40} pauseOnHover>
              {CLUBS.map((c) => (
                <span
                  key={c}
                  className="mx-4 font-heading text-3xl md:text-5xl text-transparent"
                  style={{
                    WebkitTextStroke: "1px rgba(0,229,255,0.55)",
                  }}
                >
                  {c} ·
                </span>
              ))}
            </Marquee>
          </div>
          <div className="marquee-fade mt-3">
            <Marquee gradient={false} speed={35} direction="right" pauseOnHover>
              {CLUBS.slice().reverse().map((c) => (
                <span
                  key={`r-${c}`}
                  className="mx-4 font-heading text-3xl md:text-5xl text-transparent"
                  style={{
                    WebkitTextStroke: "1px rgba(57,255,20,0.45)",
                  }}
                >
                  {c} ·
                </span>
              ))}
            </Marquee>
          </div>
        </div>

        {/* Cells & Committees */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass p-7 md:p-9"
        >
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <div className="kicker mb-3">{t("campus.cellsKicker")}</div>
              <h3 className="font-heading text-2xl md:text-3xl text-white">
                {t("campus.cellsHead")}
              </h3>
            </div>
            <div className="font-heading text-6xl text-[#00E5FF]/80">10</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5 pt-5 border-t border-white/5">
            {CELLS.map((c, i) => (
              <div key={c} className="flex items-center gap-3 text-sm text-white/80">
                <span className="font-mono text-[10px] text-[#39FF14]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{c}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CampusLife;
